// React
import { useMemo } from "react"

// Icons
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@/lib/icons"

// Dates
import { DateTime } from "@/lib/dates"

// Translate
import { useTrans } from "@/i18n"

// Context
import { useAreaGraphContext } from "./AreaGraphContext"

// UI
import { cn } from "@/lib/utils"
import { getTransitionPopoverProps } from "@/lib/animations"
import {
	Popover,
	PopoverGroup,
	PopoverButton,
	PopoverPanel,
	Transition,
} from "@/lib/ui"
import { Tooltip } from "@/components/Tooltip"
import { Button } from "@/components/Button"
import { DatePicker } from "@/components/form-controls/DatePicker"

/**
 * AreaGraphActions
 * @returns
 */
export function AreaGraphActions() {
	const { maximumDate, minimumDate, date, setDate } = useAreaGraphContext()

	// Translations
	const t = useTrans(["project", "common"])

	const currentDateTime = DateTime.fromJSDate(date)

	// is currentDateTime (current selected date) before now?
	const canGoNext = currentDateTime < maximumDate

	// is minimumDate before current date time
	const canGoPrev = currentDateTime > minimumDate

	return (
		<>
			{/* prev day button */}
			<Button
				onClick={() => {
					if (canGoPrev) {
						const nextDate = currentDateTime.minus({
							days: 1,
						})
						setDate(nextDate.toJSDate())
					}
				}}
				disabled={!canGoPrev}
				className="group relative rounded-r-none focus:z-20"
				variant="transparent"
				aria-label={t("common:navigation.graph.previous_day")}
			>
				<ChevronLeftIcon className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500" />
			</Button>

			{/* date picker */}
			<Tooltip
				content={
					"we do not store daily information for days more than a week old"
				}
			>
				<PopoverGroup className="flex items-center divide-x divide-gray-200">
					<Popover className="relative z-10 inline-block text-right">
						<PopoverButton
							className={cn(
								"group relative inline-flex items-center justify-center rounded-none",
								"border border-l-0 border-gray-300 bg-white px-3 py-2 text-sm font-medium",
								"text-gray-700 hover:bg-gray-50 hover:text-gray-900",
								"focus:ring-primary-500 focus:z-20 focus:ring-1 focus:outline-hidden",
							)}
						>
							<span className="mr-2">
								{DateTime.fromJSDate(date).toFormat(
									t(
										"project:data.production_today.date_picker.title.date_format",
									),
								)}
							</span>

							<ChevronDownIcon
								className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
								aria-hidden="true"
							/>
						</PopoverButton>
						<Transition {...getTransitionPopoverProps()}>
							<PopoverPanel className="absolute left-0 mt-2 origin-top-right lg:right-0 lg:left-auto">
								<div
									className="min-w-[300px]"
									data-testid="daterangepicker-from"
								>
									<DatePicker
										initialMonth={currentDateTime.month}
										initialYear={currentDateTime.year}
										selectedDate={currentDateTime}
										showChangeOfYear={false}
										showNavigation={false}
										months={useMemo(() => {
											if (
												maximumDate.month !==
												minimumDate.month
											) {
												return [
													minimumDate.month,
													maximumDate.month,
												]
											}
											return [maximumDate.month]
										}, [
											maximumDate.month,
											minimumDate.month,
										])}
										onSelectDate={(date) => {
											const newDate =
												DateTime.fromObject(date).endOf(
													"day",
												)
											setDate(newDate.toJSDate())
										}}
										isDateDisabled={(date) => {
											const dateTime =
												DateTime.fromObject({
													...date,
													day: date.day + 1,
												})

											// is this date within our range?
											if (
												dateTime < maximumDate &&
												dateTime >
													minimumDate.minus({
														days: 1,
													})
											) {
												return false
											}

											return true
										}}
									/>
								</div>
							</PopoverPanel>
						</Transition>
					</Popover>
				</PopoverGroup>
			</Tooltip>
			{/* next day button */}
			<Button
				onClick={() => {
					if (canGoNext) {
						const nextDate = currentDateTime.plus({
							days: 1,
						})
						setDate(nextDate.toJSDate())
					}
				}}
				className="group relative rounded-l-none focus:z-20"
				variant="transparent"
				disabled={!canGoNext}
				aria-label={t("common:navigation.graph.next_day")}
			>
				<ChevronRightIcon className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500" />
			</Button>
		</>
	)
}
